import { CpscEntity, EntityRoleCode } from '__generated__/graphql';

/**
 * Laboratory role codes that can appear in the laboratories array
 */
const LABORATORY_ROLE_CODES: EntityRoleCode[] = [
  EntityRoleCode.ITL,
  EntityRoleCode.LAB,
  EntityRoleCode.NOL,
];

/**
 * Transform CPSC entities between GraphQL schema shape and UI form shape.
 *
 * Schema shape: { manufacturers[], laboratories[], certifyingEntities[], pointOfContacts[] }
 * Form shape:   { entities[] } - flat array with entityRoleCode determining type
 *
 * This allows the UI to use a simple add/remove entities pattern while
 * maintaining compatibility with the structured GraphQL schema.
 */
export const transformCpscEntities = {
  /**
   * Transform GraphQL schema → Form state
   * Combines all 4 entity arrays into flat entities[] array for UI
   * @param {object} item - Object containing manufacturers, laboratories, certifyingEntities, pointOfContacts arrays
   * @return {CpscEntity[]} Flat array of all entities for UI form rendering
   */
  toForm: (item: {
    manufacturers?: CpscEntity[];
    laboratories?: CpscEntity[];
    certifyingEntities?: CpscEntity[];
    pointOfContacts?: CpscEntity[];
  }): CpscEntity[] => {
    const entities: CpscEntity[] = [];

    if (item.manufacturers?.length) {
      entities.push(...item.manufacturers);
    }
    if (item.laboratories?.length) {
      entities.push(...item.laboratories);
    }
    if (item.certifyingEntities?.length) {
      entities.push(...item.certifyingEntities);
    }
    if (item.pointOfContacts?.length) {
      entities.push(...item.pointOfContacts);
    }

    return entities;
  },

  /**
   * Transform Form state → GraphQL schema
   * Splits flat entities[] array into 4 structured arrays by entityRoleCode
   * @param {CpscEntity[]} entities - Flat array of entities from UI form
   * @return {object} Structured object with manufacturers, laboratories, certifyingEntities, pointOfContacts arrays
   */
  toSchema: (
    entities: CpscEntity[],
  ): {
    manufacturers?: CpscEntity[];
    laboratories?: CpscEntity[];
    certifyingEntities?: CpscEntity[];
    pointOfContacts?: CpscEntity[];
  } => {
    if (!entities?.length) {
      return {};
    }

    const result: {
      manufacturers?: CpscEntity[];
      laboratories?: CpscEntity[];
      certifyingEntities?: CpscEntity[];
      pointOfContacts?: CpscEntity[];
    } = {};

    const manufacturers = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.MF,
    );
    if (manufacturers.length) {
      result.manufacturers = manufacturers;
    }

    const laboratories = entities.filter((e) =>
      LABORATORY_ROLE_CODES.includes(e.entityRoleCode),
    );
    if (laboratories.length) {
      result.laboratories = laboratories;
    }

    const certifyingEntities = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.CE,
    );
    if (certifyingEntities.length) {
      result.certifyingEntities = certifyingEntities;
    }

    const pointsOfContact = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.PK,
    );
    if (pointsOfContact.length) {
      result.pointOfContacts = pointsOfContact;
    }

    return result;
  },
};
